import React, { useState } from "react";
import { Form, Button } from "react-bootstrap";
import { useProfile } from "../contexts/ProfileContext";
import axiosClient from "../api/axiosClient";
import Alert from "./Alert";
import { useLanguage } from "../contexts/LanguageContext";

const formatCount = (n) => {
	if (!n && n !== 0) return '—';
	if (n >= 1_000_000) return (n / 1_000_000).toFixed(1).replace(/\.0$/, '') + 'M';
	if (n >= 1_000) return (n / 1_000).toFixed(1).replace(/\.0$/, '') + 'K';
	return n.toString();
};

const ProfileCard = ({ avatar, username, fullName, bio, followers, following, posts, label, accent, isAfter, photoNote }) => {
	const L = useLanguage();
	return (
	<div style={{ flex: 1, minWidth: 0 }}>
		<div className="text-center mb-2">
			<span className="px-3 py-1 rounded-full text-xs font-bold text-white" style={{ background: accent }}>
				{label}
			</span>
		</div>
		<div className="rounded-2xl overflow-hidden" style={{
			border: isAfter ? '2px solid #22c55e' : '2px solid #e3e8ee',
			background: '#fff',
		}}>
			<div className="p-4">
				<div className="d-flex align-items-center gap-3 mb-3">
					<div style={{ position: 'relative' }}>
						<img
							src={`https://rizzup.net/proxy/${encodeURIComponent(avatar)}`}
							alt={username}
							className="rounded-circle"
							style={{ width: 64, height: 64, objectFit: 'cover', border: '3px solid #f1f5f9' }}
							onError={(e) => { e.target.onerror = null; e.target.src = '/images/default.png'; }}
						/>
						{isAfter && photoNote && (
							<div className="position-absolute" style={{
								bottom: -4, right: -4, background: '#f59e0b', borderRadius: '50%',
								width: 20, height: 20, display: 'flex', alignItems: 'center', justifyContent: 'center',
								border: '2px solid #fff',
							}}>
								<i className="fa fa-lightbulb text-white" style={{ fontSize: '0.55rem' }} />
							</div>
						)}
					</div>
					<div style={{ minWidth: 0 }}>
						<div className="font-bold text-sm" style={{ color: '#0a2540' }}>@{username}</div>
						<div className="text-xs" style={{ color: '#8898aa' }}>{fullName}</div>
					</div>
				</div>

				<div className="d-flex justify-content-around text-center mb-3 py-2 rounded-xl" style={{ background: '#f6f9fc', border: '1px solid #e3e8ee' }}>
					<div>
						<div className="font-bold text-sm" style={{ color: '#0a2540' }}>{formatCount(posts)}</div>
						<div style={{ fontSize: '0.65rem', color: '#8898aa' }}>{L.toolsProfile.posts}</div>
					</div>
					<div>
						<div className="font-bold text-sm" style={{ color: '#0a2540' }}>{formatCount(followers)}</div>
						<div style={{ fontSize: '0.65rem', color: '#8898aa' }}>{L.toolsProfile.followers}</div>
					</div>
					<div>
						<div className="font-bold text-sm" style={{ color: '#0a2540' }}>{formatCount(following)}</div>
						<div style={{ fontSize: '0.65rem', color: '#8898aa' }}>{L.toolsProfile.following}</div>
					</div>
				</div>

				<div className="rounded-xl p-3" style={{
					background: isAfter ? '#f0fdf4' : '#f6f9fc',
					border: isAfter ? '1px solid #bbf7d0' : '1px solid #e3e8ee',
					fontSize: '0.82rem',
					lineHeight: 1.6,
					color: '#334155',
					whiteSpace: 'pre-wrap',
					minHeight: 60,
				}}>
					{bio || <span style={{ color: '#94a3b8', fontStyle: 'italic' }}>{L.toolsProfile.noBio}</span>}
				</div>
			</div>
		</div>
	</div>
	);
};

const ToolsProfile = () => {
	const L = useLanguage();
	const profile = useProfile();
	const [username, setUsername] = useState(profile.instagram || '');
	const [generating, setGenerating] = useState(false);
	const [profileData, setProfileData] = useState(null);
	const [suggestions, setSuggestions] = useState(null);
	const [functionError, setFunctionError] = useState("");
	const [functionSuccess, setFunctionSuccess] = useState("");

	const scanProfile = async () => {
		if (!username.trim()) return;
		try {
			setGenerating(true);
			setProfileData(null);
			setSuggestions(null);
			setFunctionError("");

			const formData = {
				username: username.trim(),
				age: profile?.socials?.[profile?.selectedProfile]?.ageRange,
				gender: profile?.socials?.[profile?.selectedProfile]?.genderTarget,
				country: profile?.socials?.[profile?.selectedProfile]?.countries,
				niche: profile?.socials?.[profile?.selectedProfile]?.interests,
			};

			const response = await axiosClient.post("https://api.viralgpt.com/tools/profile", formData, {
				headers: { "Content-Type": "application/json" },
			});

			setProfileData(response.data.profileData);
			setSuggestions(response.data.suggestions);
			setFunctionSuccess(L.toolsProfile.success);
		} catch (e) {
			setFunctionError(L.toolsProfile.scanError(e.response?.data?.error || e.message || L.toolsProfile.unknownError));
		} finally {
			setGenerating(false);
		}
	};

	return (
		<div className="bg-white border p-6">
			<h5>{L.toolsProfile.heading}</h5>
			<p className="text-muted">{L.toolsProfile.subtitle}</p>

			<Alert variant="danger" message={functionError} show={!!functionError} onClose={() => setFunctionError("")} />
			<Alert variant="success" message={functionSuccess} show={!!functionSuccess} onClose={() => setFunctionSuccess("")} />

			<div className="d-flex gap-2 mb-4">
				<Form.Control
					type="text"
					placeholder={L.toolsProfile.usernamePlaceholder}
					value={username}
					onChange={(e) => setUsername(e.target.value)}
					onKeyDown={(e) => { if (e.key === 'Enter') scanProfile(); }}
					style={{ maxWidth: '280px' }}
				/>
				<Button
					className={generating ? "disabled bg-instagram" : "bg-instagram"}
					onClick={scanProfile}
					disabled={generating}
				>
					{generating ? L.toolsProfile.analyzing : L.toolsProfile.scanButton}
				</Button>
				<div
					style={{ fontSize: "1.5rem", display: 'flex', alignItems: 'center' }}
					className={`ms-2 fa fa-circle-notch fa-spin text-instagram ${!generating ? "d-none" : ""}`}
				/>
			</div>

			{profileData && suggestions && (
				<div style={{ animation: 'fadeIn 0.4s ease' }}>
					{/* Before / After Cards */}
					<div className="d-flex flex-column flex-md-row gap-3 mb-4" style={{ alignItems: 'stretch' }}>
						<ProfileCard
							avatar={profileData.avatar}
							username={profileData.username}
							fullName={profileData.full_name}
							bio={profileData.biography}
							followers={profileData.follower_count}
							following={profileData.following_count}
							posts={profileData.media_count}
							label={L.toolsProfile.before}
							accent="#8898aa"
							isAfter={false}
						/>

						<div className="d-flex align-items-center justify-content-center d-none d-md-flex" style={{ fontSize: '1.5rem', color: '#d1d9e0', padding: '0 4px' }}>
							<i className="fa fa-arrow-right" />
						</div>
						<div className="d-flex align-items-center justify-content-center d-md-none" style={{ fontSize: '1.2rem', color: '#d1d9e0' }}>
							<i className="fa fa-arrow-down" />
						</div>

						<ProfileCard
							avatar={profileData.avatar}
							username={profileData.username}
							fullName={suggestions.new_name}
							bio={suggestions.new_bio}
							followers={profileData.follower_count}
							following={profileData.following_count}
							posts={profileData.media_count}
							label={L.toolsProfile.after}
							accent="#22c55e"
							isAfter={true}
							photoNote={suggestions.photo_review}
						/>
					</div>

					{/* Reasoning Cards */}
					<div className="rounded-2xl p-4 mb-3" style={{ background: '#f6f9fc', border: '1px solid #e3e8ee' }}>
						<h6 className="mb-3" style={{ color: '#0a2540', fontSize: '0.9rem' }}>
							<i className="fa fa-wand-magic-sparkles text-instagram me-2" />
							{L.toolsProfile.whatWeChanged}
						</h6>
						<div className="d-flex flex-column gap-3">
							<div className="d-flex align-items-start gap-2">
								<div className="rounded-lg d-flex align-items-center justify-content-center flex-shrink-0" style={{
									width: 32, height: 32, background: 'linear-gradient(135deg, #635bff, #7c3aed)',
								}}>
									<i className="fa fa-pen text-white" style={{ fontSize: '0.7rem' }} />
								</div>
								<div>
									<div className="font-bold text-sm" style={{ color: '#0a2540' }}>{L.toolsProfile.bio}</div>
									<div className="text-sm" style={{ color: '#556987' }}>{suggestions.bio_reasoning}</div>
								</div>
							</div>
							<div className="d-flex align-items-start gap-2">
								<div className="rounded-lg d-flex align-items-center justify-content-center flex-shrink-0" style={{
									width: 32, height: 32, background: 'linear-gradient(135deg, #635bff, #7c3aed)',
								}}>
									<i className="fa fa-user text-white" style={{ fontSize: '0.7rem' }} />
								</div>
								<div>
									<div className="font-bold text-sm" style={{ color: '#0a2540' }}>{L.toolsProfile.displayName}</div>
									<div className="text-sm" style={{ color: '#556987' }}>{suggestions.name_reasoning}</div>
								</div>
							</div>
							<div className="d-flex align-items-start gap-2">
								<div className="rounded-lg d-flex align-items-center justify-content-center flex-shrink-0" style={{
									width: 32, height: 32, background: 'linear-gradient(135deg, #635bff, #7c3aed)',
								}}>
									<i className="fa fa-camera text-white" style={{ fontSize: '0.7rem' }} />
								</div>
								<div>
									<div className="font-bold text-sm" style={{ color: '#0a2540' }}>{L.toolsProfile.profilePhoto}</div>
									<div className="text-sm" style={{ color: '#556987' }}>{suggestions.photo_review}</div>
								</div>
							</div>
						</div>
					</div>

					{/* Action Items */}
					<div className="rounded-2xl p-4" style={{ background: '#fffbeb', border: '1px solid #fde68a' }}>
						<h6 className="mb-3" style={{ color: '#92400e', fontSize: '0.9rem' }}>
							<i className="fa fa-bolt me-2" style={{ color: '#f59e0b' }} />
							{L.toolsProfile.quickWins}
						</h6>
						<div className="d-flex flex-column gap-2">
							{suggestions.tips.map((tip, i) => (
								<div key={i} className="d-flex align-items-start gap-2">
									<span className="rounded-full d-flex align-items-center justify-content-center flex-shrink-0 font-bold" style={{
										width: 22, height: 22, background: '#fef3c7', color: '#d97706',
										fontSize: '0.7rem', border: '1px solid #fde68a',
									}}>
										{i + 1}
									</span>
									<span className="text-sm" style={{ color: '#78350f' }}>{tip}</span>
								</div>
							))}
						</div>
					</div>
				</div>
			)}

			<style>{`
				@keyframes fadeIn {
					from { opacity: 0; transform: translateY(12px); }
					to { opacity: 1; transform: translateY(0); }
				}
			`}</style>
		</div>
	);
};

export default ToolsProfile;
